import type { DocumentLineInput } from '../types/documents.types';

export type BulkApplyError = {
  lineIndex: number;
  reference: string;
  message: string;
};

export type BulkSnapshotEntry = {
  index: number;
  lineId: string | null;
  reference: string;
  isV2LiteWg: boolean;
  metaClone: Record<string, unknown>;
  qty: number;
};

export type BulkApplyProgress = {
  total: number;
  completed: number;
  phase: 'processing' | 'uploading';
};

export class BulkRunConcurrentEditError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BulkRunConcurrentEditError';
  }
}

export async function runBulkApplyOrchestration(params: {
  snapshotToken: number;
  startRevision: number;
  entries: BulkSnapshotEntry[];
  getOperationToken: () => number;
  canCommit: () => boolean;
  processEntry: (entry: BulkSnapshotEntry) => Promise<DocumentLineInput | null>;
  onProgress?: (progress: BulkApplyProgress) => void;
}): Promise<{ results: Map<number, DocumentLineInput>; errors: BulkApplyError[] }> {
  const results = new Map<number, DocumentLineInput>();
  const errors: BulkApplyError[] = [];
  const { entries, processEntry, onProgress, getOperationToken, snapshotToken } = params;

  onProgress?.({ total: entries.length, completed: 0, phase: 'processing' });

  for (const entry of entries) {
    if (getOperationToken() !== snapshotToken) {
      throw new BulkRunConcurrentEditError('Bulk edit was superseded by a newer run.');
    }

    try {
      const rebuilt = await processEntry(entry);
      if (rebuilt) results.set(entry.index, rebuilt);
    } catch (err) {
      errors.push({
        lineIndex: entry.index,
        reference: entry.reference || `Line ${entry.index + 1}`,
        message: err instanceof Error ? err.message : 'Bulk update failed for this line.',
      });
    }

    onProgress?.({
      total: entries.length,
      completed: results.size + errors.length,
      phase: 'processing',
    });
  }

  if (!params.canCommit()) {
    throw new BulkRunConcurrentEditError(
      'Quote was edited while bulk edit was running. No lines were updated.'
    );
  }

  return { results, errors };
}
